import React, { useCallback, useEffect, useRef, useState } from "react";

// ─── helpers ────────────────────────────────────────────────────────────────

async function apiFetch(url, options = {}) {
  const headers = { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest", ...(options.headers || {}) };
  const res = await fetch(url, { credentials: "include", ...options, headers });
  if (!res.ok) {
    let msg = `Error ${res.status}`;
    try { const j = await res.json(); msg = j.detail || JSON.stringify(j); } catch {}
    throw new Error(msg);
  }
  if (res.status === 204) return null;
  return res.json();
}

function fmt(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtDate(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function timeDiff(dt) {
  if (!dt) return "";
  const diff = Date.now() - new Date(dt).getTime();
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ago`;
  if (h > 0) return `${h}h ago`;
  return "just now";
}

function StatusPill({ status, isOverdue }) {
  const map = {
    scheduled: { label: "Scheduled", cls: "td-pill scheduled" },
    activated:  { label: "Activated",  cls: "td-pill activated" },
    conducted:  { label: "Conducted",  cls: "td-pill conducted" },
    cancelled:  { label: "Cancelled",  cls: "td-pill cancelled" },
    no_show:    { label: "No Show",    cls: "td-pill no-show" },
  };
  const entry = map[status] || { label: status, cls: "td-pill" };
  return (
    <span className={entry.cls + (isOverdue ? " overdue" : "")}>
      {isOverdue ? "⚠ Overdue" : entry.label}
    </span>
  );
}

// ─── Activity log inside detail panel ───────────────────────────────────────

function ActivityLog({ activities }) {
  if (!activities?.length) {
    return <p className="td-empty-text">No activity yet.</p>;
  }
  return (
    <ol className="td-activity-log">
      {activities.map((a) => (
        <li key={a.id} className="td-activity-entry">
          <span className={`td-activity-dot ${a.action}`} />
          <div className="td-activity-body">
            <p className="td-activity-desc">{a.description || a.action}</p>
            <p className="td-activity-meta">
              {a.actor_name || "System"} · {fmt(a.occurred_at)}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

// ─── Note modal ─────────────────────────────────────────────────────────────

function NoteModal({ appointmentId, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const ref = useRef(null);

  useEffect(() => { ref.current?.focus(); }, []);

  async function submit(e) {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    setErr("");
    try {
      const updated = await apiFetch(`/api/crm/test-drives/${appointmentId}/note/`, {
        method: "POST",
        body: JSON.stringify({ text: text.trim() }),
      });
      onSaved(updated);
    } catch (ex) {
      setErr(ex.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-panel-head">
          <h3>Add Note</h3>
          <button type="button" className="icon-button" onClick={onClose}>✕</button>
        </div>
        {err ? <div className="message-banner error">{err}</div> : null}
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12, padding: "0 0 4px" }}>
          <textarea
            ref={ref}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            placeholder="Write a note…"
            style={{ resize: "vertical", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: "0.9rem" }}
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="secondary-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-button" disabled={saving || !text.trim()}>
              {saving ? "Saving…" : "Save Note"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Cancel modal ────────────────────────────────────────────────────────────

function CancelModal({ appointmentId, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      const updated = await apiFetch(`/api/crm/test-drives/${appointmentId}/cancel/`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      });
      onSaved(updated);
    } catch (ex) {
      setErr(ex.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-panel-head">
          <h3>Cancel Test Drive</h3>
          <button type="button" className="icon-button" onClick={onClose}>✕</button>
        </div>
        {err ? <div className="message-banner error">{err}</div> : null}
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12, padding: "0 0 4px" }}>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Reason (optional)"
            autoFocus
            style={{ resize: "vertical", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: "0.9rem" }}
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="secondary-ghost" onClick={onClose}>Back</button>
            <button type="submit" className="primary-button danger" disabled={saving}>
              {saving ? "Cancelling…" : "Confirm Cancel"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── OTP Modal ────────────────────────────────────────────────────────────────

function OTPModal({ appt, onClose, onActivated }) {
  const [step, setStep]     = useState("idle"); // idle | generating | enter | verifying | done
  const [otpId, setOtpId]   = useState(null);
  const [code, setCode]     = useState("");
  const [expiresAt, setExpiresAt] = useState(null);
  const [devCode, setDevCode]     = useState(null); // only in dev
  const [err, setErr]       = useState("");
  const inputRef = useRef(null);

  async function handleGenerate() {
    setErr("");
    setStep("generating");
    try {
      const res = await apiFetch(`/api/crm/test-drives/${appt.id}/generate-otp/`, { method: "POST", body: "{}" });
      setOtpId(res.otp_id);
      setExpiresAt(res.expires_at);
      setDevCode(res._dev_code || null);
      setStep("enter");
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (ex) {
      setErr(ex.message);
      setStep("idle");
    }
  }

  async function handleVerify(e) {
    e.preventDefault();
    if (!code.trim()) return;
    setErr("");
    setStep("verifying");
    try {
      const updated = await apiFetch(`/api/crm/test-drives/${appt.id}/verify-otp/`, {
        method: "POST",
        body: JSON.stringify({ code: code.trim() }),
      });
      setStep("done");
      setTimeout(() => { onActivated(updated); onClose(); }, 1200);
    } catch (ex) {
      setErr(ex.message);
      setStep("enter");
      setCode("");
      inputRef.current?.focus();
    }
  }

  const minutesLeft = expiresAt
    ? Math.max(0, Math.round((new Date(expiresAt) - Date.now()) / 60000))
    : null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel otp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-panel-head">
          <div>
            <h3>Start Test Drive</h3>
            <p style={{ fontSize: "0.8rem", color: "#99a5ba", margin: 0 }}>{appt.customer_name} · {appt.customer_phone}</p>
          </div>
          <button type="button" className="icon-button" onClick={onClose}>✕</button>
        </div>

        <div className="otp-modal-body">
          {step === "done" ? (
            <div className="otp-success">
              <span className="otp-success-icon">✅</span>
              <p>Test drive conducted!</p>
            </div>
          ) : step === "idle" || step === "generating" ? (
            <>
              <div className="otp-info-card">
                <p className="otp-info-label">Customer</p>
                <p className="otp-info-value">{appt.customer_name}</p>
                <p className="otp-info-label" style={{ marginTop: 8 }}>Appointment Slot</p>
                <p className="otp-info-value">{fmt(appt.scheduled_for)}</p>
                <p className="otp-info-label" style={{ marginTop: 8 }}>OEM / Dealer</p>
                <p className="otp-info-value">{[appt.oem, appt.dealer_name].filter(Boolean).join(" · ")}</p>
              </div>
              <p className="otp-instruction">
                Generate an OTP — it will be sent to the customer's registered mobile number.
                Ask the customer to share the OTP to verify their presence.
              </p>
              {err ? <div className="message-banner error">{err}</div> : null}
              <button
                type="button"
                className="primary-button otp-generate-btn"
                onClick={handleGenerate}
                disabled={step === "generating"}
              >
                {step === "generating" ? "Sending OTP…" : "Generate & Send OTP"}
              </button>
            </>
          ) : (
            <>
              <div className="otp-sent-banner">
                <span>📱</span>
                <p>OTP sent to customer's mobile{minutesLeft !== null ? ` · valid for ${minutesLeft} min` : ""}</p>
              </div>
              {devCode ? (
                <div className="otp-dev-badge">Dev OTP: <strong>{devCode}</strong></div>
              ) : null}
              {err ? <div className="message-banner error">{err}</div> : null}
              <form onSubmit={handleVerify} className="otp-entry-form">
                <label className="otp-entry-label">Enter OTP from customer</label>
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="______"
                  className="otp-entry-input"
                  disabled={step === "verifying"}
                  autoComplete="one-time-code"
                />
                <div className="otp-actions">
                  <button
                    type="button"
                    className="secondary-ghost"
                    onClick={handleGenerate}
                    disabled={step === "verifying"}
                  >
                    Resend OTP
                  </button>
                  <button
                    type="submit"
                    className="primary-button"
                    disabled={step === "verifying" || code.length !== 6}
                  >
                    {step === "verifying" ? "Verifying…" : "Verify & Mark Conducted"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Detail panel (slide-in) ─────────────────────────────────────────────────

function DetailPanel({ appt, onClose, onUpdated }) {
  const [acting, setActing] = useState(null); // 'otp' | 'cancel' | 'note'
  const [reactivating, setReactivating] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [err, setErr] = useState("");

  // An overdue test drive must be reactivated before the OTP/start flow can proceed.
  const canReactivate = appt.is_overdue && ["scheduled", "activated"].includes(appt.status);
  const canStartTD = appt.status === "scheduled" && !appt.is_overdue;
  const canComplete = appt.status === "activated";  // in progress -> can be completed
  const canCancel   = !["conducted", "cancelled"].includes(appt.status);

  async function reactivate() {
    setErr("");
    setReactivating(true);
    try {
      const updated = await apiFetch(`/api/crm/test-drives/${appt.id}/reactivate/`, { method: "POST", body: "{}" });
      onUpdated(updated);
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setReactivating(false);
    }
  }

  async function complete() {
    setErr("");
    setCompleting(true);
    try {
      const updated = await apiFetch(`/api/crm/test-drives/${appt.id}/complete/`, { method: "POST", body: "{}" });
      onUpdated(updated);
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setCompleting(false);
    }
  }

  return (
    <aside className="td-detail-panel">
      <div className="td-detail-head">
        <div>
          <h3 className="td-detail-name">{appt.customer_name}</h3>
          <p className="td-detail-sub">{appt.lead_client_id} · {appt.customer_phone}</p>
        </div>
        <button type="button" className="icon-button" onClick={onClose}>✕</button>
      </div>

      <div className="td-detail-body">
        <StatusPill status={appt.status} isOverdue={appt.is_overdue} />

        <div className="td-info-grid">
          <div className="td-info-item">
            <span className="td-info-label">Slot</span>
            <span className="td-info-value">{fmt(appt.scheduled_for)}</span>
          </div>
          <div className="td-info-item">
            <span className="td-info-label">OEM</span>
            <span className="td-info-value">{appt.oem || "—"}</span>
          </div>

          <div className="td-info-item">
            <span className="td-info-label">City</span>
            <span className="td-info-value">{appt.city || appt.customer_city || "—"}</span>
          </div>
          <div className="td-info-item">
            <span className="td-info-label">Customer City</span>
            <span className="td-info-value">{appt.customer_city || "—"}</span>
          </div>
          {appt.activated_at ? (
            <div className="td-info-item">
              <span className="td-info-label">Activated At</span>
              <span className="td-info-value">{fmt(appt.activated_at)}</span>
            </div>
          ) : null}
          {appt.conducted_at ? (
            <div className="td-info-item">
              <span className="td-info-label">Conducted At</span>
              <span className="td-info-value">{fmt(appt.conducted_at)}</span>
            </div>
          ) : null}
          <div className="td-info-item">
            <span className="td-info-label">Booked By</span>
            <span className="td-info-value">{appt.assigned_by_name || "—"}</span>
          </div>
          <div className="td-info-item">
            <span className="td-info-label">Booked On</span>
            <span className="td-info-value">{fmtDate(appt.created_at)}</span>
          </div>
        </div>

        {err ? <div className="message-banner error" style={{ marginTop: 8 }}>{err}</div> : null}

        <div className="td-detail-actions">
          {canReactivate ? (
            <button
              type="button"
              className="primary-button"
              disabled={reactivating}
              onClick={reactivate}
            >
              {reactivating ? "Reactivating…" : "↻ Reactivate Test Drive"}
            </button>
          ) : null}
          {canStartTD ? (
            <button
              type="button"
              className="primary-button"
              onClick={() => setActing("otp")}
            >
              🚗 Start Test Drive
            </button>
          ) : null}
          {canComplete ? (
            <button
              type="button"
              className="primary-button success"
              disabled={completing}
              onClick={complete}
            >
              {completing ? "Completing…" : "✓ Complete Test Drive"}
            </button>
          ) : null}
          {appt.status === "conducted" ? (
            <span className="td-pill conducted" style={{ padding: "6px 14px" }}>✓ Conducted</span>
          ) : null}
          <button
            type="button"
            className="secondary-ghost"
            onClick={() => setActing("note")}
          >
            Add Note
          </button>
          {canCancel ? (
            <button
              type="button"
              className="secondary-ghost danger"
              onClick={() => setActing("cancel")}
            >
              Cancel
            </button>
          ) : null}
        </div>

        <div className="td-detail-section">
          <p className="td-section-title">Activity Log</p>
          <ActivityLog activities={appt.activities} />
        </div>
      </div>

      {acting === "otp" ? (
        <OTPModal
          appt={appt}
          onClose={() => setActing(null)}
          onActivated={(updated) => { setActing(null); onUpdated(updated); }}
        />
      ) : null}
      {acting === "note" ? (
        <NoteModal
          appointmentId={appt.id}
          onClose={() => setActing(null)}
          onSaved={(updated) => { setActing(null); onUpdated(updated); }}
        />
      ) : null}
      {acting === "cancel" ? (
        <CancelModal
          appointmentId={appt.id}
          onClose={() => setActing(null)}
          onSaved={(updated) => { setActing(null); onUpdated(updated); }}
        />
      ) : null}
    </aside>
  );
}

// ─── Test drive card ─────────────────────────────────────────────────────────

function TDCard({ appt, onSelect }) {
  return (
    <article className={`td-card ${appt.is_overdue ? "overdue" : ""}`} onClick={() => onSelect(appt)}>
      <div className="td-card-top">
        <div className="td-card-identity">
          <span className="td-card-name">{appt.customer_name}</span>
          <span className="td-card-id">{appt.lead_client_id}</span>
        </div>
        <StatusPill status={appt.status} isOverdue={appt.is_overdue} />
      </div>

      <div className="td-card-meta">
        <div className="td-card-row">
          <span className="td-card-label">Slot</span>
          <span className="td-card-value">{fmt(appt.scheduled_for)}</span>
        </div>
        <div className="td-card-row">
          <span className="td-card-label">OEM</span>
          <span className="td-card-value">{appt.oem || "—"}</span>
        </div>
        <div className="td-card-row">
          <span className="td-card-label">City</span>
          <span className="td-card-value">{appt.city || appt.customer_city || "—"}</span>
        </div>
        <div className="td-card-row">
          <span className="td-card-label">Phone</span>
          <span className="td-card-value">{appt.customer_phone}</span>
        </div>
      </div>

      <div className="td-card-foot">
        <span className="td-card-since">Booked {timeDiff(appt.created_at)}</span>
        <button type="button" className="td-card-open">View →</button>
      </div>
    </article>
  );
}

// ─── Stats bar ───────────────────────────────────────────────────────────────

function StatsBar({ items }) {
  return (
    <div className="td-stats-bar">
      {items.map(({ label, value, tone }) => (
        <div key={label} className={`td-stat-item ${tone || ""}`}>
          <strong>{value}</strong>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function TDEmpty({ label }) {
  return (
    <div className="td-empty">
      <div className="td-empty-icon">🚗</div>
      <p className="td-empty-title">No {label}</p>
      <p className="td-empty-sub">Test drives assigned to you will appear here.</p>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function DealerDashboard({ view }) {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");
  const [selected, setSelected]         = useState(null);
  const [counts, setCounts]             = useState({ pending: 0, overdue: 0 });
  const [statusFilter, setStatusFilter] = useState("all");

  const isOverdueView = view === "Overdue";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    console.log("[DealerDashboard] load() called — view:", isOverdueView ? "Overdue" : "Test Drives", "filter:", statusFilter);
    try {
      const params = new URLSearchParams();
      if (isOverdueView) {
        params.set("overdue", "1");
      } else if (statusFilter !== "all") {
        params.set("status", statusFilter);
      }

      const url = `/api/crm/test-drives/?${params}`;
      console.log("[DealerDashboard] fetching:", url);
      const data = await apiFetch(url);
      console.log("[DealerDashboard] raw response:", data);

      const list = data?.results ?? data ?? [];
      console.log("[DealerDashboard] appointments to show:", list.length, list);
      setAppointments(list);

      apiFetch("/api/crm/test-drives/pending-count/")
        .then((cnt) => { console.log("[DealerDashboard] counts:", cnt); setCounts(cnt || { pending: 0, overdue: 0 }); })
        .catch((err) => console.warn("[DealerDashboard] pending-count failed:", err.message));
    } catch (ex) {
      console.error("[DealerDashboard] load failed:", ex);
      setError(ex.message);
    } finally {
      setLoading(false);
    }
  }, [isOverdueView, statusFilter]);

  // Reload when view switches (Test Drives ↔ Overdue) or status filter changes
  useEffect(() => { load(); }, [load]);

  // Reset status filter when switching views
  useEffect(() => { setStatusFilter("all"); setSelected(null); }, [isOverdueView]);

  // Keep selected appointment in sync after actions.
  function handleUpdated(updated) {
    setAppointments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    setSelected(updated);
    // Refresh counts.
    apiFetch("/api/crm/test-drives/pending-count/").then(setCounts).catch(() => {});
  }

  const STATUS_TABS = [
    { key: "all",       label: "All" },
    { key: "scheduled", label: "Scheduled" },
    { key: "activated", label: "Activated" },
    { key: "conducted", label: "Conducted" },
    { key: "cancelled", label: "Cancelled" },
  ];

  const stats = isOverdueView
    ? [
        { label: "Overdue", value: counts.overdue, tone: "danger" },
        { label: "Total shown", value: appointments.length, tone: "" },
      ]
    : [
        { label: "Pending", value: counts.pending, tone: "amber" },
        { label: "Overdue", value: counts.overdue, tone: "danger" },
        { label: "Total", value: appointments.length, tone: "" },
      ];

  const displayItems = appointments;

  return (
    <div className={`td-shell ${selected ? "td-shell-split" : ""}`}>
      {/* ── Left: list pane ── */}
      <div className="td-list-pane">
        {/* Header */}
        <div className="td-list-header">
          <div>
            <h1 className="td-list-title">
              {isOverdueView ? "Overdue Test Drives" : "My Test Drives"}
            </h1>
            <p className="td-list-sub">
              {isOverdueView
                ? "Appointments that have passed their scheduled time."
                : "Test drives assigned to your dealership."}
            </p>
          </div>
          <button type="button" className="secondary-ghost" onClick={load} disabled={loading}>
            {loading ? "Loading…" : "↻ Refresh"}
          </button>
        </div>

        {/* Stats */}
        <StatsBar items={stats} />

        {/* Status filter tabs — only on main view */}
        {!isOverdueView ? (
          <div className="report-tab-strip td-tab-strip">
            {STATUS_TABS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={`report-tab-button ${statusFilter === key ? "active" : ""}`}
                onClick={() => setStatusFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}

        {error ? <div className="message-banner error">{error}</div> : null}

        {/* Card grid */}
        {loading ? (
          <div className="td-loading">
            {[1, 2, 3].map((n) => <div key={n} className="td-card-skeleton" />)}
          </div>
        ) : displayItems.length === 0 ? (
          <TDEmpty label={isOverdueView ? "overdue test drives" : "test drives"} />
        ) : (
          <div className="td-card-grid">
            {displayItems.map((appt) => (
              <TDCard
                key={appt.id}
                appt={appt}
                onSelect={setSelected}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Right: detail panel ── */}
      {selected ? (
        <DetailPanel
          appt={selected}
          onClose={() => setSelected(null)}
          onUpdated={handleUpdated}
        />
      ) : null}
    </div>
  );
}
